/* eslint-disable no-unused-vars */
import classNames from 'classnames';
import React, {
	ReactNode,
	Ref,
	RefObject,
	forwardRef,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef,
} from 'react';
import { useSize } from 'ahooks';
import { BaseLoadingV2 } from '@shared/components/base/BaseLoadingV2';

import styles from './index.module.less';
import { ColumnVirtualList } from './ColumnVirtualList';

export type WidthMapColumns = {
	minWidth: number;
	maxWidth: number;
	columnGap: number; // 列间距
	rowGap: number; // 行间距
	bothSideMargin: number; // 内容两边的间距
	marginLeft?: number;
	marginRight?: number;
}[];

export interface WaterfallItem {
	width: number;
	height: number;
}

export interface WaterFallRef<T> {
	columnsData: (T & { scrollTop: number })[][];
}

export interface WaterfallProps<T> extends I_BaseComponentProps {
	ref?: Ref<WaterFallRef<T>>;
	containerRef: RefObject<HTMLDivElement>;
	columnClassName?: string;
	scrollRef?: RefObject<HTMLDivElement> | HTMLElement;
	data?: T[];
	title?: ReactNode;
	offsetHeight?: number | ((item: T) => number);
	savePreData?: boolean;
	titleClassName?: string;
	isVirtualList?: boolean;
	columnOptions?: WidthMapColumns;
	loading?: boolean;
	noMore?: boolean;
	loadingMore?: boolean;
	loadingRender?: ReactNode;
	loadingMoreRender?: ReactNode;
	noMoreTipRender?: ReactNode;
	emptyStatusRender?: ReactNode;
	expandHeight?: number;
	onDataChange?: (data: T[]) => void; // 当屏幕宽度发生变化触发列数数据变化时回调
	onFirstItemResize?: (dimensions: { width: number; height: number }) => void; // 新增属性
	/** 你可以使用var(--card-width)控制卡片内容大小 */
	// originIndex 为 data 在原始列表中的索引
	waterfallItemRender: (data: T, index: number, originIndex?: number) => ReactNode;
}

export function waterFallSort<T>(cumulativeData: T[][]) {
	const sortedRows = [];
	let i = 0;
	let j = 0;
	const maxLength = Math.max(...cumulativeData.map((item) => item.length));

	while (i < cumulativeData.length && j < maxLength) {
		const currentData = cumulativeData[i][j];
		if (currentData) {
			sortedRows.push(currentData);
		}
		i++;
		if (i === cumulativeData.length) {
			i = 0;
			j++;
		}
	}
	return sortedRows;
}

export function parseWaterFallData<T extends WaterfallItem>(
	data: T[],
	columnCount: number,
	containerWidth: number,
	columnOptions: WidthMapColumns,
	offsetHeight: number | ((item: T) => number) = 0,
): (T & { scrollTop: number, dataIndex: number })[][] {
	const cumulativeHeight: number[] = new Array(columnCount).fill(0);
	const cumulativeData: (T & { scrollTop: number, dataIndex: number })[][] = Array.from(
		new Array(columnCount),
		() => [],
	);

	let currentColumnOptions = columnOptions[0];
	columnOptions.forEach((item) => {
		const { minWidth, maxWidth } = item;
		if (containerWidth >= minWidth && containerWidth <= maxWidth) {
			currentColumnOptions = item;
		}
	});

	const {
		columnGap = 0,
		rowGap = 0,
		bothSideMargin = 0,
		marginLeft = 0,
		marginRight = 0,
	} = currentColumnOptions ?? {};
	const margin = bothSideMargin === 0 ? marginLeft + marginRight : bothSideMargin * 2;
	const finalWidth = containerWidth - (columnCount - 1) * columnGap - margin;
	const columnWidth = finalWidth / columnCount;
	data.forEach((item, dataIndex) => {
		let minHeightColumnIndex = 0;
		for (let i = 0; i < cumulativeHeight.length; i++) {
			const currentHeight = cumulativeHeight[i];
			const minHeight = cumulativeHeight[minHeightColumnIndex];
			if (currentHeight < minHeight) {
				minHeightColumnIndex = i;
			}
		}

		const scale = item.height / item.width;
		const itemHeight = columnWidth * scale;
		cumulativeData[minHeightColumnIndex].push({
			...item,
			scrollTop: cumulativeHeight[minHeightColumnIndex],
			dataIndex,
		});
		const curOffsetHeight = typeof offsetHeight === 'function' ? offsetHeight(item) : offsetHeight;
		cumulativeHeight[minHeightColumnIndex] += itemHeight + curOffsetHeight + rowGap;
	});
	return cumulativeData;
}

function WaterFallList<T extends WaterfallItem>(
	props: WaterfallProps<T>,
	ref?: Ref<WaterFallRef<T>>,
) {
	const INIT_DATA = useRef<T[]>([]).current;
	const savedData = useRef<T[]>([]);
	const {
		className,
		columnClassName,
		style,
		containerRef,
		scrollRef,
		data = INIT_DATA,
		title,
		offsetHeight = 0,
		titleClassName,
		noMore,
		savePreData = false,
		isVirtualList = false,
		columnOptions = [],
		loading = false,
		loadingRender = <BaseLoadingV2 style={{ marginTop: 200 }} />,
		loadingMoreRender = <BaseLoadingV2 style={{ margin: '50px 0' }} />,
		noMoreTipRender = <div className={classNames(styles.noHasMore)}></div>,
		emptyStatusRender = <></>,
		loadingMore = false,
		expandHeight = 0,
		onDataChange,
		onFirstItemResize,
		waterfallItemRender,
	} = props;

	// - 某些时候如果要让弹窗销毁，组件还能保存
	// - 但下面这个 useSize 会在弹窗关闭时 width 为 0，这里引入一个辅助变量
	const lastRenderWidth = useRef(0);
	const { width: _containerWidth = window.innerWidth } = useSize(containerRef) ?? {};
	const containerWidth = _containerWidth || lastRenderWidth.current;
	lastRenderWidth.current = containerWidth;

	const columnCount = useMemo(() => {
		const n = columnOptions.length;
		for (let i = n - 1; i >= 0; i--) {
			const { minWidth, maxWidth } = columnOptions[i];
			if (containerWidth >= minWidth && containerWidth <= maxWidth) {
				return i + 1;
			}

			if (i + 1 === columnOptions.length && containerWidth > maxWidth) {
				return columnOptions.length;
			}
		}
		return 1;
	}, [columnOptions, containerWidth]);

	const margin = useMemo(() => {
		const columnIndex = columnCount - 1;
		const currentColumnOption = columnOptions[columnIndex] ?? {};
		const marginLeft = currentColumnOption.marginLeft ?? 0;
		const marginRight = currentColumnOption.marginRight ?? 0;
		return marginLeft + marginRight;
	}, [columnOptions, columnCount]);

	const columnWidth = useMemo(() => containerWidth / columnCount, [containerWidth, columnCount]);
	//styles瀑布流使用该变量
	// Used for styles waterfall layout
	const styleRenderedColumnWidth = useMemo(() => {
		const columnGap = columnOptions[0].columnGap + (10 - columnCount);
		return (containerWidth - columnGap * (columnCount - 1) - margin) / columnCount - 4;
	}, [containerWidth, columnCount, margin]);

	const renderedColumnWidth = useMemo(() => {
		const columnGap = columnOptions[0].columnGap;
		return (containerWidth - columnGap * (columnCount - 1)) / columnCount;
	}, [containerWidth, columnCount]);

	const computedStyles = useMemo(() => {
		const columnIndex = columnCount - 1;

		const currentColumnOption = columnOptions[columnIndex] ?? {};

		const waterFallContainer: React.CSSProperties = {
			padding: `0 ${currentColumnOption.bothSideMargin}px`,
			...style,
		};

		const waterFallContent = {
			maxWidth: currentColumnOption.maxWidth,
			columnGap: currentColumnOption.columnGap,
			'--card-width': columnWidth + 'px',
			//styles瀑布流使用该变量
			'--column-width': styleRenderedColumnWidth + 'px',
		} as React.CSSProperties;

		const waterFallColumnItem: React.CSSProperties = {
			rowGap: currentColumnOption.rowGap,
		};

		return {
			waterFallContainer,
			waterFallContent,
			waterFallColumnItem,
		};
	}, [columnCount, columnWidth, style]);

	const displayData = useMemo(() => {
		if (loading && savePreData && savedData.current.length > 0) {
			return savedData.current;
		}
		savedData.current = data;
		return data;
	}, [loading, savePreData, data]);

	const columnsData = useMemo(() => {
		const cumulativeData = parseWaterFallData(
			data,
			columnCount,
			containerWidth,
			columnOptions,
			offsetHeight,
		);
		return cumulativeData;
	}, [data, columnCount]);

	useImperativeHandle(ref, () => {
		return {
			columnsData,
		};
	});

	useEffect(() => {
		onDataChange?.(waterFallSort<T>(columnsData));
	}, [columnsData]);

	const firstItemRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (firstItemRef.current && onFirstItemResize) {
			const resizeObserver = new ResizeObserver(() => {
				const dimensions = {
					width: firstItemRef.current!.offsetWidth,
					height: firstItemRef.current!.offsetHeight,
				};
				onFirstItemResize(dimensions);
			});
			resizeObserver.observe(firstItemRef.current);
			return () => {
				resizeObserver.disconnect();
			};
		}
	}, [firstItemRef.current, onFirstItemResize]);

	return (
		<div
			className={classNames(styles.waterFallContainer, className)}
			style={computedStyles.waterFallContainer}
		>
			{title && (
				<div
					className={classNames(styles.title, titleClassName)}
					style={computedStyles.waterFallContent}
				>
					{title}
				</div>
			)}

			{loading && (!savePreData || displayData?.length === 0) ? (
				loadingRender
			) : displayData?.length ? (
				<>
					<div
						className={classNames('waterFallContent', styles.waterFallContent)}
						style={computedStyles.waterFallContent}
					>
						{/* 每一列渲染 */}
						{columnsData.map((column, columnIndex) => {
							return isVirtualList ? (
								<ColumnVirtualList<T>
									className={classNames(
										'waterFallColumnItem',
										styles.waterFallColumnItem,
										columnClassName,
									)}
									style={computedStyles.waterFallColumnItem}
									originalList={column}
									key={columnIndex}
									scrollContainerRef={scrollRef}
									itemRender={waterfallItemRender}
									expandHeight={expandHeight}
									renderedColumnWidth={renderedColumnWidth}
								/>
							) : (
								<div
									key={columnIndex}
									className={classNames(
										'waterFallColumnItem',
										styles.waterFallColumnItem,
										columnClassName,
									)}
									style={computedStyles.waterFallColumnItem}
								>
									{/* 每一个渲染 */}
									{column.map((item, itemIndex) => {
										const itemRef = columnIndex === 0 && itemIndex === 0 ? firstItemRef : null;
										return (
											<div ref={itemRef} key={itemIndex}>
												{waterfallItemRender(item, itemIndex, item.dataIndex)}
											</div>
										);
									})}
								</div>
							);
						})}
					</div>
					{loadingMore && loadingMoreRender}
					{!loading && !loadingMore && noMore && data?.length && noMoreTipRender}
				</>
			) : (
				emptyStatusRender
			)}
		</div>
	);
}
/**
 * @deprecated 移动端 MasonryGrid 已稳定可用！虚拟列表性能更好，无需提前指定元素大小
 */
export const WaterFall = forwardRef(WaterFallList) as <T extends WaterfallItem>(
	props: WaterfallProps<T>,
) => ReturnType<typeof WaterFallList>;
